/**
 * UI Scene
 *
 * HUD overlay for displaying height, timer, and other UI elements.
 * Runs in parallel with GameScene.
 */

import Phaser from 'phaser';
import { GAME_CONSTANTS, GAME_EVENTS } from '@/config/constants';
import { ICONS } from '@/config/icons';
import { SoundManager } from '@/systems/SoundManager';
import { SafeAreaUtil } from '@/utils/SafeAreaUtil';
import { SettingsManager } from '@/systems/SettingsManager';

export class UIScene extends Phaser.Scene {
  private heightText?: Phaser.GameObjects.Text;
  private timerText?: Phaser.GameObjects.Text;
  private safeAreaTopPadding: number = 0;

  // Phase 5: Highscore and new record
  private highscoreText?: Phaser.GameObjects.Text;
  private fpsText?: Phaser.GameObjects.Text;
  private newRecordText?: Phaser.GameObjects.Text;
  private newRecordTween?: Phaser.Tweens.Tween;

  // Collectibles counter
  private collectiblesText?: Phaser.GameObjects.Text;
  private collectiblesIcon?: Phaser.GameObjects.Image;
  private lastCollectedCount: number = -1; // Track to prevent tween spam
  private collectiblesTween?: Phaser.Tweens.Tween;

  constructor() {
    super({ key: 'UIScene' });
  }

  preload(): void {
    // Load SVG icons
    this.load.svg('icon-pause', '/pause_48dp_E3E3E3_FILL1_wght400_GRAD0_opsz48.svg', { scale: 1 });
    this.load.svg('icon-teleport', '/icon-teleport.svg', { scale: 1 });
    this.load.svg('icon-calibrate', '/icon-calibrate.svg', { scale: 1 });
  }

  create(): void {
    // Initialize sound manager
    SoundManager.initialize();

    // Get safe area insets for iOS notch/home indicator
    this.safeAreaTopPadding = SafeAreaUtil.getTopPadding();

    console.log(`📱 UI Safe Area Top Padding: ${this.safeAreaTopPadding}px`);

    const { width } = this.cameras.main;

    // === NEW HEADER BAR (simplified for pause redesign) ===
    // Row 1: PAUSE button (full width, 64px tall)
    // Row 2: CALIBRATE button (full width, 64px tall)
    // Row 3: TELEPORT button (left side, square, invisible but clickable)
    const headerY = 10 + this.safeAreaTopPadding;
    const buttonHeight = 64; // 64px tall buttons
    const margin = 10;

    // === ROW 1: PAUSE BUTTON ===
    const row1Y = headerY;
    this.createIconButton(
      margin,
      row1Y,
      width - margin * 2, // Full width minus margins
      buttonHeight,
      'icon-pause',
      'Pause',
      () => this.pauseGame()
    );

    // === ROW 2: CALIBRATE BUTTON ===
    const row2Y = row1Y + buttonHeight + 5;
    this.createIconButton(
      margin,
      row2Y,
      width - margin * 2, // Full width minus margins
      buttonHeight,
      'icon-calibrate',
      'Calibrate',
      () => this.calibrateTilt()
    );

    // === ROW 3: TELEPORT BUTTON (invisible, left side, square) ===
    const row3Y = row2Y + buttonHeight + 5;
    const teleportSize = buttonHeight; // Square button (64x64)
    this.createIconButton(
      margin,
      row3Y,
      teleportSize,
      teleportSize,
      'icon-teleport',
      '',
      () => this.teleportToNextZone(),
      true // transparent = true (invisible but still clickable)
    );

    // === INFO ROW (below header bar) ===
    // Position below the three-row header, moved closer to calibrate button
    const infoY = row2Y + buttonHeight + 5; // Positioned right after calibrate button (row2)

    // Height counter (left side) - 24px
    this.heightText = this.add.text(20, infoY, 'Height: 0m', {
      fontSize: '24px',
      color: GAME_CONSTANTS.UI.COLOR_PRIMARY,
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      stroke: '#000000',
      strokeThickness: 2,
      resolution: 2, // Fixed resolution for consistent crisp rendering
    });
    this.heightText.setDepth(100);

    // Phase 5: Highscore indicator (below height) - 18px
    const highscoreY = infoY + 30;
    this.highscoreText = this.add.text(20, highscoreY, 'Best: 0m', {
      fontSize: '18px',
      color: GAME_CONSTANTS.UI.COLOR_ACCENT,
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      stroke: '#000000',
      strokeThickness: 2,
      resolution: 2, // Fixed resolution for consistent crisp rendering
    });
    this.highscoreText.setDepth(100);

    // FPS indicator (below highscore) - 18px
    const fpsY = highscoreY + 25;
    this.fpsText = this.add.text(20, fpsY, 'FPS: 60', {
      fontSize: '18px',
      color: '#00ff00',
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      stroke: '#000000',
      strokeThickness: 2,
      resolution: 2, // Fixed resolution for consistent crisp rendering
    });
    this.fpsText.setDepth(100);
    this.fpsText.setVisible(SettingsManager.getShowFpsCounter()); // Respect setting

    // Timer (right side, same height as height text) - 18px
    // Left-aligned at fixed position to prevent movement
    this.timerText = this.add.text(width - 115, infoY, '00:00:00:00', {
      fontSize: '18px',
      color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      stroke: '#000000',
      strokeThickness: 2,
      resolution: 2, // Fixed resolution for consistent crisp rendering
    });
    this.timerText.setOrigin(0, 0); // Left-aligned, position stays fixed
    this.timerText.setDepth(100);

    // Collectibles counter (right side, below timer) - 18px
    const collectiblesY = infoY + 25;
    this.collectiblesText = this.add.text(width - 20, collectiblesY, '0/0', {
      fontSize: '18px',
      color: '#FFD700', // Gold color
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      stroke: '#000000',
      strokeThickness: 2,
      resolution: 2, // Fixed resolution for consistent crisp rendering
    });
    this.collectiblesText.setOrigin(1, 0);
    this.collectiblesText.setDepth(100);

    // Diamond icon for collectibles (to the left of the text)
    const iconSize = 16;
    const iconSpacing = 4;
    this.collectiblesIcon = this.add.image(
      width - 20 - this.collectiblesText.width - iconSpacing - iconSize / 2,
      collectiblesY + this.collectiblesText.height / 2,
      ICONS.DIAMOND
    );
    this.collectiblesIcon.setDisplaySize(iconSize, iconSize);
    this.collectiblesIcon.setTint(0xffd700); // Gold tint
    this.collectiblesIcon.setDepth(100);

    // Phase 5: New record notification (center screen, initially hidden)
    this.newRecordText = this.add.text(
      width / 2,
      this.cameras.main.height / 2 - 100,
      'NEW RECORD!',
      {
        fontSize: `${GAME_CONSTANTS.UI.FONT_SIZE_LARGE}px`,
        color: GAME_CONSTANTS.UI.COLOR_ACCENT,
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        stroke: '#000000',
        strokeThickness: 6,
        resolution: 2, // Fixed resolution for consistent crisp rendering
      }
    );
    this.newRecordText.setOrigin(0.5);
    this.newRecordText.setDepth(200);
    this.newRecordText.setVisible(false);
  }

  private pauseGame(): void {
    // Emit pause event to GameScene
    this.game.events.emit(GAME_EVENTS.GAME_PAUSE);
  }

  private calibrateTilt(): void {
    // Emit event to GameScene to calibrate tilt
    const gameScene = this.scene.get('GameScene');
    if (gameScene) {
      gameScene.events.emit('calibrate-tilt');
    }
  }

  private teleportToNextZone(): void {
    // Only allow teleport if enabled in dev settings
    if (!SettingsManager.getEnableTeleport()) {
      return; // Teleport is disabled, button does nothing
    }

    // Emit event to GameScene to teleport to next zone
    const gameScene = this.scene.get('GameScene');
    if (gameScene) {
      gameScene.events.emit('teleport-next-zone');
    }
  }

  update(_time: number, _delta: number): void {
    // Update UI elements
    // Will be connected to game state in Phase 1
  }

  public updateHeight(height: number): void {
    if (this.heightText) {
      this.heightText.setText(`Height: ${Math.round(height)}m`);
    }
  }

  public updateTimer(milliseconds: number): void {
    if (this.timerText) {
      const hours = Math.floor(milliseconds / 3600000);
      const minutes = Math.floor((milliseconds % 3600000) / 60000);
      const seconds = Math.floor((milliseconds % 60000) / 1000);
      const centiseconds = Math.floor((milliseconds % 1000) / 10); // 2 digits for ms

      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${centiseconds.toString().padStart(2, '0')}`;
      this.timerText.setText(timeString);
    }
  }

  /**
   * Update highscore display (Phase 5)
   */
  public updateHighscore(maxHeight: number): void {
    if (this.highscoreText) {
      this.highscoreText.setText(`Best: ${Math.round(maxHeight)}m`);
    }
  }

  /**
   * Update collectibles counter
   * PERFORMANCE FIX (v0.8.6): Only animate when count actually changes
   */
  public updateCollectibles(collected: number, total: number): void {
    if (this.collectiblesText && this.collectiblesIcon) {
      this.collectiblesText.setText(`${collected}/${total}`);

      // Update icon position (to the left of the text)
      const width = this.cameras.main.width;
      const iconSize = 16;
      const iconSpacing = 4;
      const collectiblesY = this.collectiblesText.y;
      this.collectiblesIcon.setPosition(
        width - 20 - this.collectiblesText.width - iconSpacing - iconSize / 2,
        collectiblesY + this.collectiblesText.height / 2
      );

      // Only create pulse animation when count actually changes AND increases
      // This prevents 2,700+ tween accumulation over 3 minutes (15 tweens/sec)
      // Also prevents weird intro animation when game first starts (0/0 -> 0/45)
      if (collected !== this.lastCollectedCount && collected > this.lastCollectedCount && this.lastCollectedCount >= 0) {
        this.lastCollectedCount = collected;

        // Stop any existing tween before creating new one
        if (this.collectiblesTween) {
          this.collectiblesTween.stop();
        }

        // Pulse animation when collecting (apply to both text and icon)
        this.collectiblesTween = this.tweens.add({
          targets: [this.collectiblesText, this.collectiblesIcon],
          scale: 1.2,
          duration: 150,
          yoyo: true,
          ease: 'Power2',
        });
      } else if (collected !== this.lastCollectedCount) {
        // Update lastCollectedCount without animation
        this.lastCollectedCount = collected;
      }
    }
  }

  /**
   * Update FPS display
   */
  public updateFPS(fps: number): void {
    if (this.fpsText) {
      // Update visibility based on setting
      const shouldShow = SettingsManager.getShowFpsCounter();
      this.fpsText.setVisible(shouldShow);

      if (shouldShow) {
        // Color code based on FPS
        let color = '#00ff00'; // Green for 55+
        if (fps < 30) {
          color = '#ff0000'; // Red for <30
        } else if (fps < 55) {
          color = '#ffaa00'; // Orange for 30-54
        }
        this.fpsText.setColor(color);
        this.fpsText.setText(`FPS: ${fps}`);
      }
    }
  }

  /**
   * Show the new record notification with pulse animation (Phase 5)
   */
  public showNewRecordNotification(): void {
    if (!this.newRecordText) return;

    this.newRecordText.setVisible(true);

    // Stop any existing tween
    if (this.newRecordTween) {
      this.newRecordTween.stop();
    }

    // Pulse animation
    this.newRecordTween = this.tweens.add({
      targets: this.newRecordText,
      scale: { from: 1.0, to: 1.2 },
      alpha: { from: 1.0, to: 0.7 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Hide the new record notification (Phase 5)
   */
  public hideNewRecordNotification(): void {
    if (!this.newRecordText) return;

    this.newRecordText.setVisible(false);

    // Stop tween
    if (this.newRecordTween) {
      this.newRecordTween.stop();
      this.newRecordTween = undefined;
    }
  }

  /**
   * Create an icon button with optional label
   * Returns a container with background, icon, and label
   */
  private createIconButton(
    x: number,
    y: number,
    width: number,
    height: number,
    iconKey: string,
    label: string,
    onClick: () => void,
    transparent: boolean = false
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Black background (or transparent for dev tools)
    const bg = this.add.rectangle(0, 0, width, height, 0x000000);
    bg.setOrigin(0, 0);

    // Make background 100% transparent if specified (but still clickable)
    if (transparent) {
      bg.setAlpha(0.01); // Very low alpha but not zero - keeps it interactive
    }

    // IMPORTANT: Set interactive AFTER alpha to ensure it works with transparent objects
    bg.setInteractive({ useHandCursor: true });

    container.add(bg);

    // Icon size and font size - double for main buttons (PAUSE, CALIBRATE), normal for dev tools
    const iconSize = transparent ? 24 : 48; // Double size for visible buttons
    const fontSize = transparent ? '14px' : '28px'; // Double size for visible buttons

    // Create icon and label, then center them together
    const icon = this.add.image(0, 0, iconKey);
    icon.setOrigin(0.5, 0.5);
    icon.setDisplaySize(iconSize, iconSize);
    icon.setTint(0xffffff); // White tint

    // Make icon transparent too if button is transparent
    if (transparent) {
      icon.setAlpha(0);
    }

    // White label (if provided)
    let labelText: Phaser.GameObjects.Text | undefined;
    if (label) {
      labelText = this.add.text(0, 0, label, {
        fontSize: fontSize,
        color: '#ffffff',
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontStyle: 'bold',
        resolution: 2, // Fixed resolution for consistent crisp rendering
      });
      labelText.setOrigin(0, 0.5); // Left-align the label text

      // Make label transparent too if button is transparent
      if (transparent) {
        labelText.setAlpha(0);
      }
    }

    // Position icon and label centered together
    if (labelText) {
      // Calculate total width of icon + gap + label
      const gap = 15; // Gap between icon and label
      const totalWidth = iconSize + gap + labelText.width;

      // Center the group within the button
      const startX = (width - totalWidth) / 2;

      // Position icon and label
      icon.setPosition(startX + iconSize / 2, height / 2);
      labelText.setPosition(startX + iconSize + gap, height / 2);
    } else {
      // Icon only - center it
      icon.setPosition(width / 2, height / 2);
    }

    container.add(icon);
    if (labelText) {
      container.add(labelText);
    }

    // Click handler
    bg.on('pointerdown', onClick);

    // Hover effects (only if not transparent)
    if (!transparent) {
      bg.on('pointerover', () => {
        bg.setFillStyle(0x222222);
      });

      bg.on('pointerout', () => {
        bg.setFillStyle(0x000000);
      });
    }

    container.setDepth(100);
    return container;
  }
}
